import React from 'react';

const UserDetailsModal = ({ user, isOpen, onClose, onRemoveCourse }) => {
  if (!isOpen || !user) return null;

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
          <h2 className="text-2xl font-bold text-slate-900">User Details</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
          >
            &times;
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4">
          {/* Basic Information */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-slate-800 mb-3">Basic Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50 p-4 rounded-lg">
              <div>
                <p className="text-sm text-gray-600">Username</p>
                <p className="font-medium text-slate-900">{user.username}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Email</p>
                <p className="font-medium text-slate-900">{user.email}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Full Name</p>
                <p className="font-medium text-slate-900">{user.full_name || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Phone Number</p>
                <p className="font-medium text-slate-900">{user.phone || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Role</p>
                <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                  user.role === 'admin' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                }`}>
                  {user.role}
                </span>
              </div>
              <div>
                <p className="text-sm text-gray-600">Status</p>
                <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                  user.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  {user.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div>
                <p className="text-sm text-gray-600">Joined</p>
                <p className="font-medium text-slate-900">{formatDate(user.date_joined)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Last Login</p>
                <p className="font-medium text-slate-900">
                  {user.last_login ? formatDate(user.last_login) : 'Never'}
                </p>
              </div>
            </div>
          </div>

          {/* Statistics */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-slate-800 mb-3">Statistics</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">Enrolled Courses</p>
                <p className="text-3xl font-bold text-blue-600">{user.enrolled_courses_count || 0}</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">Quiz Attempts</p>
                <p className="text-3xl font-bold text-green-600">{user.quiz_attempts_count || 0}</p>
              </div>
            </div>
          </div>

          {/* Enrollments */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-slate-800 mb-3">Enrollments</h3>
            {user.enrollments && user.enrollments.length > 0 ? (
              <div className="space-y-2">
                {user.enrollments.map((enrollment, index) => (
                  <div key={index} className="bg-gray-50 p-3 rounded-lg flex justify-between items-center">
                    <div className="flex-1">
                      <p className="font-medium text-slate-900">{enrollment.course_name}</p>
                      <p className="text-sm text-gray-600">Enrolled: {formatDate(enrollment.enrolled_at)}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        enrollment.status === 'approved'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {enrollment.status}
                      </span>
                      {onRemoveCourse && (
                        <button
                          onClick={() => onRemoveCourse(user.id, enrollment.course.id, enrollment.course_name)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Remove course"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-gray-50 p-4 rounded-lg text-center text-gray-600">
                No enrollments yet
              </div>
            )}
          </div>

          {/* Quiz Attempts */}
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-slate-800 mb-3">Recent Quiz Attempts</h3>
            {user.quiz_attempts && user.quiz_attempts.length > 0 ? (
              <div className="space-y-2">
                {user.quiz_attempts.map((attempt, index) => (
                  <div key={index} className="bg-gray-50 p-3 rounded-lg flex justify-between items-center">
                    <div>
                      <p className="font-medium text-slate-900">{attempt.quiz_name}</p>
                      <p className="text-sm text-gray-600">Attempted: {formatDate(attempt.attempted_at)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-slate-900">{attempt.score}/{attempt.total}</p>
                      <p className="text-sm text-gray-600">
                        {Math.round((attempt.score / attempt.total) * 100)}%
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-gray-50 p-4 rounded-lg text-center text-gray-600">
                No quiz attempts yet
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserDetailsModal;
